import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { IconButton } from '@mui/material';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { PropsWithChildren, useMemo } from 'react';
import { MIXED_COLORS } from '../../../../../components/Charts/MixedChart/MixedChart';
import { RESTRICTED_COLUMN_GROUPS } from '../../../../../components/ColumnManagement/constants';
import { shouldHideRestrictedColumnGroups } from '../../../../../components/ColumnManagement/utils';
import { BasicDialog } from '../../../../../components/Dialog/BasicDialog';
import { PermissionGatedWrapper } from '../../../../../components/Fallback/PermissionGatedWrapper';
import { ROUTES } from '../../../../../constants/RouteNameMapping';
import { useShowSummaryV2 } from '../../../../CompanyProfiles2/Summary/useShowSummaryV2';
import { PermissionKey } from '../../../../../services/PermissionAndRolesKeys';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { IField } from '../../../../../data-models/field2.data-model';
import { kpiConfigByIdMapAtom, kpiConfigByNameAtom } from '../../../../../services/state/KPIConfigState';
import {
  selectedColumnsGridDisplayFieldsOrgState,
  selectedKPIChartFieldsOrgState,
} from '../../../../AdminPanel/DataDisplay/DataDisplayState';
import { ChartPanel } from '../../../../CompanyProfiles2/Summary/ChartPanel';
import { SummarySection } from '../../../../CompanyProfiles2/Summary/SummarySection';
import { summaryChartBodyCss } from '../../../../CompanyProfiles2/Summary/summary-layout.styles';
import { SecondaryForbiddenMessage } from '../../../../CompanyProfiles2/Summary/constants/forbidden-messages.constants';
import { NoDataMessage2 } from '../../../components/Messages/HelpMessage';
import { selectedSummaryChartState } from '../../../state/UIStateJ';
import { useProfileBasePath } from '../../../useProfileBasePath';
import { ExpandedMixedChart } from './ExpandedPerfChart';
import { IChartFieldParams, MixedPerfChart } from './MixedPerfChart';

function useSelectedKPIs() {
  const kpisByName = useAtomValue(kpiConfigByNameAtom);
  const kpisById = useAtomValue(kpiConfigByIdMapAtom);
  const config = useAtomValue(selectedKPIChartFieldsOrgState);

  const kpiSelectedOne =
    config?.selectedKPIOne != null && kpisById.has(config.selectedKPIOne)
      ? kpisById.get(config.selectedKPIOne)
      : kpisByName.get('Total Revenue');

  const kpiSelectedTwo = config?.selectedKPITwo != null ? kpisById.get(config.selectedKPITwo) : undefined;

  const kpi1ShowBudget = config?.kpi1ShowBudget ?? false;
  const singleYAxis = config?.singleYAxis ?? false;

  return { kpiSelectedOne, kpiSelectedTwo, kpi1ShowBudget, singleYAxis };
}

export function TotalRevenueChartTitle(props: PropsWithChildren & { kpiSelectedOne: IField<unknown> }) {
  const showSummaryV2 = useShowSummaryV2();
  const profileUrl = useProfileBasePath();
  const performanceUrl = `${profileUrl}/${ROUTES.COMPANY_PROFILE_PERFORMANCE}`;
  const setSelectedChart = useSetAtom(selectedSummaryChartState);

  if (!showSummaryV2) {
    return (
      <ChartPanel
        title={props.kpiSelectedOne.displayName}
        showMore={{ url: performanceUrl, title: 'Go to Performance' }}
        style={{ gap: 0 }}
        onExpand={() => setSelectedChart('ttrv')}
      >
        {props.children}
      </ChartPanel>
    );
  }

  return (
    <SummarySection
      title={props.kpiSelectedOne.displayName}
      childrenContainerStyle={summaryChartBodyCss}
      showMore={{ url: performanceUrl, title: 'Go to Performance' }}
      actions={
        <IconButton
          onClick={() => setSelectedChart('ttrv')}
          color='secondary'
          sx={{ transform: 'rotate(45deg)' }}
          aria-label='expand chart'
        >
          <UnfoldMoreIcon />
        </IconButton>
      }
    >
      {props.children}
    </SummarySection>
  );
}

export function TotalRevenueChart() {
  const fields = useTotalRevenueChartFields();
  const { kpiSelectedOne } = useSelectedKPIs();
  const columnsToHide = useAtomValue(selectedColumnsGridDisplayFieldsOrgState);
  const hasRestrictedKPIsAccess = useMemo(
    () =>
      shouldHideRestrictedColumnGroups([RESTRICTED_COLUMN_GROUPS.KPIS], columnsToHide)[
        RESTRICTED_COLUMN_GROUPS.KPIS
      ],
    [columnsToHide]
  );
  const [selectedChart, setSelectedChart] = useAtom(selectedSummaryChartState);
  const open = selectedChart === 'ttrv';

  if (!kpiSelectedOne || hasRestrictedKPIsAccess) {
    return (
      <PermissionGatedWrapper
        permissions={[PermissionKey.canViewKpis]}
        fallbackMessage={SecondaryForbiddenMessage}
      >
        <NoDataMessage2 />
      </PermissionGatedWrapper>
    );
  }

  return (
    <PermissionGatedWrapper
      permissions={[PermissionKey.canViewKpis]}
      fallbackMessage={SecondaryForbiddenMessage}
    >
      <TotalRevenueChartTitle kpiSelectedOne={kpiSelectedOne}>
        {open ? (
          <BasicDialog
            open={open}
            titleComponent={kpiSelectedOne?.displayName}
            lightDismiss
            onClose={() => setSelectedChart(null)}
          >
            {open ? <ExpandedMixedChart kpis={fields} /> : <></>}
          </BasicDialog>
        ) : (
          <></>
        )}
        <MixedPerfChart
          fields={fields}
          title={kpiSelectedOne?.displayName}
          staticOptions={{
            yAxis: { splitNumber: 2, axisLabel: { fontSize: 9 } },
            tooltip: {
              confine: true,
            },
          }}
        />
      </TotalRevenueChartTitle>
    </PermissionGatedWrapper>
  );
}

export function useTotalRevenueChartFields() {
  const { kpiSelectedOne, kpiSelectedTwo, kpi1ShowBudget, singleYAxis } = useSelectedKPIs();

  return useMemo(() => {
    if (!kpiSelectedOne) {
      return [];
    }
    const res: IChartFieldParams[] = [
      {
        field: kpiSelectedOne,
        kpiSection: KpiSection.actual,
        options: {
          color: MIXED_COLORS[0],
          barGap: 0,
          emphasis: {
            focus: 'series',
          },
          name: 'Actual',
          showBackground: false,
          type: 'bar',
        },
      },
    ] as IChartFieldParams[];
    if (kpi1ShowBudget) {
      res.push({
        field: kpiSelectedOne,
        kpiSection: KpiSection.budget,
        options: {
          color: MIXED_COLORS[1],
          barGap: 0,
          emphasis: {
            focus: 'series',
          },
          name: 'Budget',
          showBackground: false,
          type: 'bar',
        },
      });
    }
    if (kpiSelectedTwo) {
      res.push({
        field: kpiSelectedTwo,
        kpiSection: KpiSection.actual,
        options: {
          color: MIXED_COLORS[2],
          barGap: 0,
          emphasis: {
            focus: 'series',
          },
          name: kpiSelectedTwo.displayName,
          showBackground: false,
          type: 'line',
          yAxisIndex: singleYAxis ? 0 : 1,
          lineStyle: {
            type: 'dashed',
          },
        },
      });
    }
    return res;
  }, [kpiSelectedOne, kpiSelectedTwo, kpi1ShowBudget, singleYAxis]);
}
